import {
  Box,
  Button,
  Card,
  CardBody,
  CircularProgress,
  Flex,
  Grid,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import FAIcon from '@components/FAIcon';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekday from 'dayjs/plugin/weekday';
import { useCallback, useEffect, useState } from 'react';
import useAPI from '../../hooks/api';
import StreamTimeline from './StreamTimeline';

dayjs.extend(weekOfYear);
dayjs.extend(weekday);

const StreamRecords = ({ creatorId }) => {
  const [streamRecords, setStreamRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('七天');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [vodFiles, setVodFiles] = useState([]);
  const [loadingVodFiles, setLoadingVodFiles] = useState(false);
  const [statistics, setStatistics] = useState({
    totalDuration: 0,
    totalPrivateDuration: 0,
    totalDiamonds: 0,
    totalGiftDiamonds: 0,
    streamCount: 0,
  });
  
  const api = useAPI();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const getDateRange = useCallback(() => {
    const now = dayjs();
    let startDate, endDate;

    switch (selectedPeriod) {
      case '今日':
        startDate = now.startOf('day');
        endDate = now.endOf('day');
        break;
      case '昨日':
        startDate = now.subtract(1, 'day').startOf('day');
        endDate = now.subtract(1, 'day').endOf('day');
        break;
      case '七天':
        startDate = now.subtract(6, 'day').startOf('day');
        endDate = now.endOf('day');
        break;
      case '30天':
        startDate = now.subtract(29, 'day').startOf('day');
        endDate = now.endOf('day');
        break;
      case '自定義':
        startDate = customStartDate ? dayjs(customStartDate).startOf('day') : now.subtract(7, 'day').startOf('day');
        endDate = customEndDate ? dayjs(customEndDate).endOf('day') : now.endOf('day');
        break;
      default:
        startDate = now.subtract(6, 'day').startOf('day');
        endDate = now.endOf('day');
        break;
    }

    return { 
      startDate: startDate.toDate(), 
      endDate: endDate.toDate() 
    };
  }, [selectedPeriod, customStartDate, customEndDate]);

  const loadStreamRecords = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true);
      setStreamRecords([]);
      setNextCursor(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const { startDate, endDate } = getDateRange();
      const params = {
        creatorId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        ...(nextCursor && !reset ? { nextCursor } : {}),
      };

      console.log('Loading stream records with params:', params);
      
      const response = await api.getCreatorStreamRecords(params);
      
      if (response) {
        const newRecords = response.data || [];
        setStreamRecords(prev => reset ? newRecords : [...prev, ...newRecords]);
        setNextCursor(response.nextCursor);
      }
    } catch (error) {
      console.error('Error loading stream records:', error);
      toast({
        title: '載入失敗',
        description: '無法載入直播紀錄',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [api, creatorId, getDateRange, nextCursor, toast]);

  const loadStatistics = useCallback(async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const params = {
        creatorId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };

      console.log('Loading statistics with params:', params);
      
      const response = await api.getCreatorStreamRecordsStatistics(params);
      
      if (response && response.data) {
        const stats = response.data;
        setStatistics({
          totalDuration: stats.totalDuration || 0,
          totalPrivateDuration: stats.totalPrivateDuration || 0,
          totalDiamonds: stats.totalDiamonds || 0,
          totalGiftDiamonds: stats.totalGiftDiamonds || 0,
          streamCount: stats.streamCount || 0,
        });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast({
        title: '載入失敗',
        description: '無法載入統計資料',
        status: 'error',
        duration: 3000,
      });
    }
  }, [api, creatorId, getDateRange, toast]);

  const handleRecordClick = (record) => {
    setSelectedRecord(record);
    setVodFiles([]);
    loadVodFiles(record.id);
    onOpen();
  };

  const loadVodFiles = async (streamId) => {
    setLoadingVodFiles(true);
    try {
      const response = await api.getStreamVodFiles(streamId);
      if (response && response.files) {
        setVodFiles(response.files);
      }
    } catch (error) {
      console.error('Error loading VOD files:', error);
      toast({
        title: '載入失敗',
        description: '無法載入VOD檔案',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoadingVodFiles(false);
    }
  };

  const handleVodDownload = async (fileName) => {
    try {
      const response = await api.getStreamVodFileDownloadUrl(selectedRecord.id, fileName);
      if (response && response.downloadUrl) {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.download = fileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        // Temporarily add to DOM
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: '下載開始',
          description: `正在下載 ${fileName}`,
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error downloading VOD file:', error);
      toast({
        title: '下載失敗',
        description: '無法下載VOD檔案',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period !== '自定義') {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  useEffect(() => {
    loadStreamRecords(true);
    loadStatistics();
  }, [selectedPeriod, customStartDate, customEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date filter section
  const renderDateFilter = () => (
    <Box bg="gray.700" p={4} borderRadius="md" mb={4}>
      <Grid templateColumns="1fr 1fr 1fr auto" gap={4} alignItems="center">
        <GridItem>
          <Text fontSize="sm" mb={1}>開始日期</Text>
          <Input
            type="date"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
            disabled={selectedPeriod !== '自定義'}
            bg="gray.600"
            border="1px solid"
            borderColor="gray.500"
          />
        </GridItem>
        <GridItem>
          <Text fontSize="sm" mb={1}>結束日期</Text>
          <Input
            type="date"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            disabled={selectedPeriod !== '自定義'}
            bg="gray.600"
            border="1px solid"
            borderColor="gray.500"
          />
        </GridItem>
        <GridItem>
          <Text fontSize="sm" mb={1}>快速選擇</Text>
          <Select
            value={selectedPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            bg="gray.600"
            border="1px solid"
            borderColor="gray.500"
          >
            <option value="今日">今日</option>
            <option value="昨日">昨日</option>
            <option value="七天">七天</option>
            <option value="30天">30天</option>
            <option value="自定義">自定義</option>
          </Select>
        </GridItem>
      </Grid>
    </Box>
  );

  // Statistics summary
  const renderStatistics = () => (
    <Box bg="gray.700" p={4} borderRadius="md" mb={4}>
      <Grid templateColumns="repeat(5, 1fr)" gap={4}>
        <GridItem>
          <VStack>
            <Text fontSize="xl" fontWeight="bold">{statistics.streamCount}</Text>
            <Text fontSize="sm" color="gray.300">直播場次</Text>
          </VStack>
        </GridItem>
        <GridItem>
          <VStack>
            <Flex align="center">
              <FAIcon group="solid" name="clock" color="blue.400" mr={1} />
              <Text fontSize="xl" fontWeight="bold">{formatDuration(statistics.totalDuration)}</Text>
            </Flex>
            <Text fontSize="sm" color="gray.300">總時長</Text>
          </VStack>
        </GridItem>
        <GridItem>
          <VStack>
            <Flex align="center">
              <FAIcon group="solid" name="lock" color="orange.400" mr={1} />
              <Text fontSize="xl" fontWeight="bold">{formatDuration(statistics.totalPrivateDuration)}</Text>
            </Flex>
            <Text fontSize="sm" color="gray.300">私密時長</Text>
          </VStack>
        </GridItem>
        <GridItem>
          <VStack>
            <Flex align="center">
              <FAIcon group="solid" name="gift" color="purple.400" mr={1} />
              <Text fontSize="xl" fontWeight="bold">{statistics.totalGiftDiamonds}</Text>
            </Flex>
            <Text fontSize="sm" color="gray.300">禮物鑽石</Text>
          </VStack>
        </GridItem>
        <GridItem>
          <VStack>
            <Text fontSize="xl" fontWeight="bold" color="orange.400">{statistics.totalDiamonds}</Text>
            <Text fontSize="sm" color="gray.300">總鑽石</Text>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );

  // Stream record item
  const renderStreamRecord = (record) => (
    <Card
      key={record.id}
      bg="gray.600"
      cursor="pointer"
      onClick={() => handleRecordClick(record)}
      _hover={{ bg: 'gray.500' }}
      mb={2}
    >
      <CardBody p={4}>
        <Flex justify="space-between" align="start" mb={2}>
          <Box flex={1}>
            <Text fontWeight="bold" fontSize="md" mb={1}>
              {record.title || '直播標題'}
            </Text>
            <Text fontSize="sm" color="gray.300">
              {dayjs(record.startTime * 1000).format('YYYY/MM/DD HH:mm')}
            </Text>
          </Box>
          <Flex align="center">
            <Text fontSize="lg" fontWeight="bold" color="orange.400">
              {record.totalDiamonds}
            </Text>
            <Text fontSize="sm" color="gray.300" ml={1}>鑽石</Text>
          </Flex>
        </Flex>
        
        <Grid templateColumns="repeat(4, 1fr)" gap={4} fontSize="sm">
          <GridItem>
            <Flex align="center">
              <FAIcon group="solid" name="clock" color="gray.400" mr={1} />
              <Text>{formatDuration(record.totalDuration)}</Text>
            </Flex>
          </GridItem>
          <GridItem>
            <Flex align="center">
              <FAIcon group="solid" name="lock" color="gray.400" mr={1} />
              <Text>{formatDuration(record.privateDuration)}</Text>
            </Flex>
          </GridItem>
          <GridItem>
            <Flex align="center">
              <FAIcon group="solid" name="users" color="gray.400" mr={1} />
              <Text>{record.viewerCount}</Text>
            </Flex>
          </GridItem>
          <GridItem>
            <Flex align="center">
              <FAIcon group="solid" name="gift" color="gray.400" mr={1} />
              <Text>{record.giftDiamonds}</Text>
            </Flex>
          </GridItem>
        </Grid>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <CircularProgress isIndeterminate />
      </Flex>
    );
  }

  return (
    <Box>
      {renderDateFilter()}
      {renderStatistics()}
      
      <Box>
        {streamRecords.length === 0 ? (
          <Flex justify="center" align="center" h="200px" direction="column">
            <FAIcon group="solid" name="eye" fontSize="48px" color="gray.400" mb={4} />
            <Text color="gray.400">沒有找到直播紀錄</Text>
          </Flex>
        ) : (
          <>
            {streamRecords.map(renderStreamRecord)}
            {nextCursor && (
              <Flex justify="center" mt={4}>
                <Button
                  onClick={() => loadStreamRecords(false)}
                  isLoading={loadingMore}
                  colorScheme="blue"
                  size="sm"
                >
                  載入更多
                </Button>
              </Flex>
            )}
          </>
        )}
      </Box>

      {/* Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader>
            直播詳細資料
            {selectedRecord && (
              <Text fontSize="sm" color="gray.300" fontWeight="normal">
                {dayjs(selectedRecord.startTime * 1000).format('YYYY/MM/DD HH:mm')}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedRecord ? (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" mb={2}>基本資訊</Text>
                  <Grid templateColumns="repeat(3, 1fr)" gap={3} fontSize="sm">
                    <GridItem>
                      <Text color="gray.300">標題</Text>
                      <Text fontWeight="medium">{selectedRecord.title || '無標題'}</Text>
                    </GridItem>
                    <GridItem>
                      <Text color="gray.300">開始時間</Text>
                      <Text fontWeight="medium">{dayjs(selectedRecord.startTime * 1000).format('MM/DD HH:mm')}</Text>
                    </GridItem>
                    <GridItem>
                      <Text color="gray.300">總時長</Text>
                      <Text fontWeight="medium">{formatDuration(selectedRecord.totalDuration)}</Text>
                    </GridItem>
                    <GridItem>
                      <Text color="gray.300">私密時長</Text>
                      <Text fontWeight="medium">{formatDuration(selectedRecord.privateDuration)}</Text>
                    </GridItem>
                    <GridItem>
                      <Text color="gray.300">觀看人數</Text>
                      <Text fontWeight="medium">{selectedRecord.viewerCount}</Text>
                    </GridItem>
                    <GridItem>
                      <Text color="gray.300">平均觀看</Text>
                      <Text fontWeight="medium">{selectedRecord.averageViewerCount}</Text>
                    </GridItem>
                  </Grid>
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={2}>VOD檔案</Text>
                  {loadingVodFiles ? (
                    <Flex justify="center" align="center" h="100px">
                      <CircularProgress isIndeterminate size="30px" />
                    </Flex>
                  ) : vodFiles.length === 0 ? (
                    <Flex justify="center" align="center" h="100px" direction="column">
                      <FAIcon group="solid" name="video-slash" fontSize="24px" color="gray.400" mb={2} />
                      <Text color="gray.400" fontSize="sm">沒有VOD檔案</Text>
                    </Flex>
                  ) : (
                    <VStack spacing={2} align="stretch">
                      {vodFiles.map((file, index) => (
                        <Flex
                          key={index}
                          align="center"
                          justify="space-between"
                          p={3}
                          bg="gray.700"
                          borderRadius="md"
                          _hover={{ bg: 'gray.600' }}
                        >
                          <Box flex={1}>
                            <Flex align="center" mb={1}>
                              <FAIcon group="solid" name="file-video" color="blue.400" mr={2} />
                              <Text fontWeight="medium">{file.name}</Text>
                            </Flex>
                            <Flex align="center" fontSize="sm" color="gray.400">
                              <Text mr={4}>{formatFileSize(file.size)}</Text>
                              <Text>{dayjs(file.updated).format('YYYY/MM/DD HH:mm')}</Text>
                            </Flex>
                          </Box>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            leftIcon={<FAIcon group="solid" name="download" />}
                            onClick={() => handleVodDownload(file.name)}
                          >
                            下載
                          </Button>
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={2}>收益資訊</Text>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem>
                      <Text fontSize="sm" color="gray.300">禮物鑽石</Text>
                      <Text color="purple.400">{selectedRecord.giftDiamonds}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.300">私密觀看鑽石</Text>
                      <Text color="orange.400">{selectedRecord.privateDiamonds}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.300">私密每分鐘費用</Text>
                      <Text>{selectedRecord.pricePerMinute}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.300">總鑽石</Text>
                      <Text color="orange.400" fontWeight="bold">{selectedRecord.totalDiamonds}</Text>
                    </GridItem>
                  </Grid>
                </Box>

                <Box>
                  <StreamTimeline streamRecord={selectedRecord} />
                </Box>
              </VStack>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StreamRecords;